'use client';

import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { trackEmerald } from '../analytics';
import { useFlowDispatch } from '../FlowProvider';
import {
  createChargeRumble,
  hapThud,
  initHapticsPad,
  installDelegatedTapHaptics,
} from '../haptics';
import { useSphereHandle } from '../sphereContext';
import { HoldHint, OrbButton, OrbWrap } from '../styles';

const HOLD_MS = 950;

const toUV = (
  clientX: number,
  clientY: number,
  el: HTMLElement,
): [number, number] => {
  const rc = el.getBoundingClientRect();
  return [
    ((clientX - rc.left) / rc.width - 0.5) * 2,
    (0.5 - (clientY - rc.top) / rc.height) * 2,
  ];
};

export type IntroOrbProps = {
  orbWrapRef: RefObject<HTMLDivElement | null>;
  revealed: boolean;
  perched: boolean;
  /** Canvas host — rendered inside OrbWrap so it inherits the perch transition. */
  children?: ReactNode;
};

/** Act 1 — press and hold (pointer, or Space/Enter) to charge the sphere. */
export const IntroOrb = ({
  orbWrapRef,
  revealed,
  perched,
  children,
}: IntroOrbProps) => {
  const sphereRef = useSphereHandle();
  const dispatch = useFlowDispatch();

  const [pressed, setPressed] = useState(false);
  const holdingRef = useRef(false);
  const holdStartRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const burstFiredRef = useRef(false);
  const timeoutsRef = useRef<number[]>([]);
  const holdHintRef = useRef<HTMLDivElement>(null);
  const chargeProgressRef = useRef(0);
  // Built in a ref, not an effect, so it exists from the very first pointerdown.
  const rumbleRef = useRef<ReturnType<typeof createChargeRumble> | null>(null);
  if (!rumbleRef.current) {
    rumbleRef.current = createChargeRumble(() => chargeProgressRef.current);
  }
  const rumble = rumbleRef.current;

  const stopTick = () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const clearTimers = () => {
    timeoutsRef.current.forEach((id) => window.clearTimeout(id));
    timeoutsRef.current = [];
  };

  const burst = () => {
    if (burstFiredRef.current) return;
    burstFiredRef.current = true;
    trackEmerald('emerald_orb_charged');
    rumble.stop();
    hapThud();
    sphereRef.current?.triggerBurst();
    sphereRef.current?.setHolding(false);
    dispatch({ type: 'REVEAL' });
    timeoutsRef.current.push(
      window.setTimeout(() => dispatch({ type: 'PERCH' }), 350),
      window.setTimeout(() => dispatch({ type: 'BURST' }), 1200),
    );
  };

  const holdTick = () => {
    if (!holdingRef.current) return;
    const now = performance.now();
    const p = Math.min(1, (now - holdStartRef.current) / HOLD_MS);
    chargeProgressRef.current = p;
    sphereRef.current?.setChargeProgress(p * p);
    // Written straight to inline style, never setState — charging must not
    // trigger a React re-render.
    if (holdHintRef.current) {
      holdHintRef.current.style.opacity = String(Math.max(0, 1 - p * 1.2));
    }
    if (p >= 1) {
      holdingRef.current = false;
      setPressed(false);
      burst();
      return;
    }
    rafRef.current = requestAnimationFrame(holdTick);
  };

  const startHold = (uv: [number, number]) => {
    if (burstFiredRef.current || holdingRef.current) return;
    holdingRef.current = true;
    holdStartRef.current = performance.now();
    chargeProgressRef.current = 0;
    // The hint's CSS transition carries a 1.1s delay for the reveal fade; it has
    // to be off during the hold or the per-frame opacity writes are swallowed.
    if (holdHintRef.current) holdHintRef.current.style.transition = 'none';
    setPressed(true);
    sphereRef.current?.setHolding(true);
    sphereRef.current?.triggerRipple(uv);
    rumble.start();
    holdTick();
  };

  const endHold = () => {
    if (!holdingRef.current) return;
    holdingRef.current = false;
    stopTick();
    rumble.stop();
    setPressed(false);
    sphereRef.current?.setHolding(false);
    sphereRef.current?.setChargeProgress(0);
    if (holdHintRef.current) {
      holdHintRef.current.style.transition = '';
      holdHintRef.current.style.opacity = '1';
    }
  };

  // Post-reveal a press should ripple, not start another hold-to-charge.
  // burst() sets this too; this keeps them in sync if `revealed` flips elsewhere.
  useEffect(() => {
    burstFiredRef.current = revealed;
  }, [revealed]);

  useEffect(() => {
    const up = () => endHold();
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') endHold();
    };
    document.addEventListener('visibilitychange', onVisibility);
    const disposePad = initHapticsPad();
    const disposeTapHaptics = installDelegatedTapHaptics();
    return () => {
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      document.removeEventListener('visibilitychange', onVisibility);
      stopTick();
      clearTimers();
      rumble.stop();
      disposePad();
      disposeTapHaptics();
    };
  }, []);

  return (
    <>
      <OrbWrap
        ref={orbWrapRef}
        $revealed={revealed}
        $perched={perched}
        $pressed={pressed}
      >
        {children}
        <OrbButton
          type="button"
          aria-label="The UltraSphere. Press and hold, or hold Space, to begin."
          onPointerDown={(e) => {
            if (burstFiredRef.current) {
              sphereRef.current?.triggerRipple(
                toUV(e.clientX, e.clientY, e.currentTarget),
              );
              return;
            }
            startHold(toUV(e.clientX, e.clientY, e.currentTarget));
          }}
          onKeyDown={(e) => {
            if (e.repeat) return;
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              startHold([0, 0]);
            }
          }}
          onKeyUp={(e) => {
            if (e.key === ' ' || e.key === 'Enter') endHold();
          }}
        />
      </OrbWrap>
      <HoldHint ref={holdHintRef} $visible={!revealed} aria-hidden="true">
        press and hold
      </HoldHint>
    </>
  );
};
